import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Divider
} from '@mui/material';
import apiClient from '../services/apiClient';

interface Profile {
  id: string;
  name: string;
  description: string;
  environment: string;
  isActive: boolean;
  hasCredentials: boolean;
  isCustom?: boolean;
}

interface ProfileConfig {
  baseUrl: string;
  authBaseUrl: string;
  authType: 'oauth' | 'basic';
  consumerKey: string;
  consumerSecret: string;
  username: string;
  password: string;
  nsisCode: string;
  authorityId: string;
}

interface ProfileEditForm {
  name: string;
  description: string;
  environment: string;
  config: ProfileConfig;
}

interface ProfileEditDialogProps {
  open: boolean;
  profile: Profile | null;
  onClose: () => void;
  onProfileUpdated: () => void;
}

const ProfileEditDialog: React.FC<ProfileEditDialogProps> = ({
  open,
  profile,
  onClose,
  onProfileUpdated
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [form, setForm] = useState<ProfileEditForm>({
    name: '',
    description: '',
    environment: 'custom',
    config: {
      baseUrl: '',
      authBaseUrl: '',
      authType: 'oauth',
      consumerKey: '',
      consumerSecret: '',
      username: '',
      password: '',
      nsisCode: '202011400',
      authorityId: 'STRPRIV45'
    }
  });

  // Carica i dati del profilo quando si apre il dialog
  useEffect(() => {
    if (open && profile) {
      loadProfileData();
    }
  }, [open, profile]);

  const loadProfileData = async () => {
    if (!profile) return;

    try {
      setLoading(true);
      setError('');

      // Carica la configurazione completa dal backend
      const response = await apiClient.get(`/recup-profiles/${profile.id}/config`);
      
      if (response.data.success) {
        const profileConfig = response.data.data;
        setForm({
          name: profileConfig.name,
          description: profileConfig.description,
          environment: profileConfig.environment,
          config: {
            baseUrl: profileConfig.config.baseUrl,
            authBaseUrl: profileConfig.config.authBaseUrl,
            authType: profileConfig.config.authType || 'oauth',
            consumerKey: '', // Non esporre per sicurezza
            consumerSecret: '', // Non esporre per sicurezza
            username: '', // Non esporre per sicurezza
            password: '', // Non esporre per sicurezza
            nsisCode: profileConfig.config.nsisCode,
            authorityId: profileConfig.config.authorityId
          }
        });
      } else {
        setError(response.data.error || 'Errore durante il caricamento del profilo');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Errore durante il caricamento');
    } finally {
      setLoading(false);
    }
  };

  const getDefaultBaseUrl = (profileId: string) => {
    switch (profileId) {
      case 'production':
        return 'https://gwapi-recup.servicelazio.it/recup/1.0';
      case 'staging':
        return 'https://recup-webapi-appmobile-collaudo.regione.lazio.it:443';
      case 'test':
        return 'https://recup-test.regione.lazio.it';
      default:
        return 'https://gwapi-recup.servicelazio.it/recup/1.0';
    }
  };

  const getDefaultAuthUrl = (profileId: string) => {
    switch (profileId) {
      case 'production':
        return 'https://iam.regione.lazio.it:443';
      case 'staging':
        return 'https://iam-collaudo.regione.lazio.it:443';
      case 'test':
        return 'https://iam-test.regione.lazio.it:443';
      default:
        return 'https://iam.regione.lazio.it:443';
    }
  };

  const handleSubmit = async () => {
    if (!profile) return;

    try {
      setLoading(true);
      setError('');

      // Validazione base
      if (!form.name || !form.config.baseUrl || !form.config.authBaseUrl) {
        setError('Nome, Base URL e Auth URL sono obbligatori');
        return;
      }

      // Validazione credenziali in base al tipo di autenticazione - solo se i campi vengono modificati E non sono vuoti
      if (form.config.authType === 'oauth') {
        // Per OAuth: se viene fornito un campo non vuoto, devono essere forniti entrambi
        const hasOAuthData = (form.config.consumerKey && form.config.consumerKey.trim() !== '') || 
                            (form.config.consumerSecret && form.config.consumerSecret.trim() !== '');
        if (hasOAuthData && (!form.config.consumerKey || !form.config.consumerSecret || 
            form.config.consumerKey.trim() === '' || form.config.consumerSecret.trim() === '')) {
          setError('Entrambi Consumer Key e Consumer Secret sono necessari per OAuth');
          return;
        }
      } else if (form.config.authType === 'basic') {
        // Per Basic Auth: se viene fornito un campo non vuoto, devono essere forniti entrambi
        const hasBasicData = (form.config.username && form.config.username.trim() !== '') || 
                            (form.config.password && form.config.password.trim() !== '');
        if (hasBasicData && (!form.config.username || !form.config.password || 
            form.config.username.trim() === '' || form.config.password.trim() === '')) {
          setError('Entrambi Username e Password sono necessari per Basic Auth');
          return;
        }
      }

      // Valida URL solo se sono stati modificati
      if (form.config.baseUrl && form.config.authBaseUrl) {
        try {
          new URL(form.config.baseUrl);
          new URL(form.config.authBaseUrl);
        } catch {
          setError('Formato URL non valido');
          return;
        }
      }

      // Se il profilo è custom, usa l'endpoint per aggiornarlo
      if (profile.isCustom) {
        const response = await apiClient.put(`/recup-profiles/custom/${profile.id}`, form);
        if (!response.data.success) {
          setError(response.data.error || 'Errore durante l\'aggiornamento del profilo');
          return;
        }
      } else {
        // Per i profili predefiniti, aggiorna solo le credenziali via variabili ambiente
        const credentialsData: any = {
          authType: form.config.authType,
          baseUrl: form.config.baseUrl,
          authBaseUrl: form.config.authBaseUrl,
          nsisCode: form.config.nsisCode,
          authorityId: form.config.authorityId
        };

        // Aggiungi SOLO le credenziali appropriate in base al tipo di auth E solo se non vuote
        if (form.config.authType === 'oauth') {
          // Per OAuth, invia solo consumerKey e consumerSecret se non vuoti
          if (form.config.consumerKey && form.config.consumerKey.trim() !== '') {
            credentialsData.consumerKey = form.config.consumerKey;
          }
          if (form.config.consumerSecret && form.config.consumerSecret.trim() !== '') {
            credentialsData.consumerSecret = form.config.consumerSecret;
          }
        } else if (form.config.authType === 'basic') {
          // Per Basic Auth, invia solo username e password se non vuoti
          if (form.config.username && form.config.username.trim() !== '') {
            credentialsData.username = form.config.username;
          }
          if (form.config.password && form.config.password.trim() !== '') {
            credentialsData.password = form.config.password;
          }
        }

        // Usa endpoint e payload diverso per profili custom vs predefiniti
        let response;
        if (profile.isCustom) {
          // Per profili custom, invia l'intero oggetto profilo
          response = await apiClient.put(`/recup-profiles/custom/${profile.id}`, {
            name: form.name,
            description: form.description,
            environment: form.environment,
            config: {
              ...credentialsData,
              authType: form.config.authType,
              consumerKey: form.config.consumerKey || undefined,
              consumerSecret: form.config.consumerSecret || undefined,
              username: form.config.username || undefined,
              password: form.config.password || undefined
            }
          });
        } else {
          // Per profili predefiniti, usa solo credentialsData
          response = await apiClient.put(`/recup-profiles/${profile.id}/credentials`, credentialsData);
        }
        
        if (!response.data.success) {
          setError(response.data.error || 'Errore durante l\'aggiornamento delle credenziali');
          return;
        }
      }

      onProfileUpdated();
      onClose();
      resetForm();

    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Errore durante l\'aggiornamento');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      description: '',
      environment: 'custom',
      config: {
        baseUrl: '',
        authBaseUrl: '',
        authType: 'oauth',
        consumerKey: '',
        consumerSecret: '',
        username: '',
        password: '',
        nsisCode: '202011400',
        authorityId: 'STRPRIV45'
      }
    });
    setError('');
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      resetForm();
    }
  };

  const updateForm = (path: string, value: string) => {
    setForm(prev => {
      const newForm = { ...prev };
      const keys = path.split('.');
      let current: any = newForm;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newForm;
    });
  };

  if (!profile) return null;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Modifica Profilo: {profile.name}
        {profile.isCustom && <Typography variant="caption" display="block" color="text.secondary">
          Profilo personalizzato
        </Typography>}
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && !error && (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress />
          </Box>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              label="Nome Profilo"
              value={form.name}
              onChange={(e) => updateForm('name', e.target.value)}
              disabled={loading || !profile.isCustom}
              helperText={!profile.isCustom ? "Nome non modificabile per profili predefiniti" : ""}
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Ambiente</InputLabel>
              <Select
                value={form.environment}
                onChange={(e) => updateForm('environment', e.target.value)}
                disabled={loading || !profile.isCustom}
                label="Ambiente"
              >
                <MenuItem value="custom">Custom</MenuItem>
                <MenuItem value="production">Production</MenuItem>
                <MenuItem value="staging">Staging</MenuItem>
                <MenuItem value="test">Test</MenuItem>
                <MenuItem value="development">Development</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Descrizione"
              value={form.description}
              onChange={(e) => updateForm('description', e.target.value)}
              disabled={loading}
              multiline
              rows={2}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Configurazione Endpoint
              </Typography>
            </Divider>
          </Grid>

          {/* URLs */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Base URL API RECUP"
              value={form.config.baseUrl}
              onChange={(e) => updateForm('config.baseUrl', e.target.value)}
              disabled={loading}
              placeholder="https://gwapi-recup.servicelazio.it/recup/1.0"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Auth Base URL"
              value={form.config.authBaseUrl}
              onChange={(e) => updateForm('config.authBaseUrl', e.target.value)}
              disabled={loading}
              placeholder="https://iam.regione.lazio.it:443"
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Configurazione Autenticazione
              </Typography>
            </Divider>
          </Grid>

          {/* Tipo di Autenticazione */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Tipo Autenticazione</InputLabel>
              <Select
                value={form.config.authType}
                onChange={(e) => updateForm('config.authType', e.target.value)}
                disabled={loading}
                label="Tipo Autenticazione"
              >
                <MenuItem value="oauth">OAuth 2.0 (Produzione)</MenuItem>
                <MenuItem value="basic">Basic Authentication (Test/Collaudo)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Alert severity={form.config.authType === 'oauth' ? 'success' : 'info'} sx={{ height: '100%' }}>
              <Typography variant="body2">
                {form.config.authType === 'oauth' 
                  ? 'Usa Consumer Key/Secret per ambiente di produzione'
                  : 'Usa Username/Password per ambienti di test'
                }
              </Typography>
            </Alert>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Credenziali di Accesso
              </Typography>
            </Divider>
          </Grid>

          {/* Credenziali OAuth */}
          {form.config.authType === 'oauth' && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Consumer Key"
                  value={form.config.consumerKey}
                  onChange={(e) => updateForm('config.consumerKey', e.target.value)}
                  disabled={loading}
                  type="password"
                  placeholder="Lascia vuoto per non modificare"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Consumer Secret"
                  value={form.config.consumerSecret}
                  onChange={(e) => updateForm('config.consumerSecret', e.target.value)}
                  disabled={loading}
                  type="password"
                  placeholder="Lascia vuoto per non modificare"
                />
              </Grid>
            </>
          )}

          {/* Credenziali Basic Auth */}
          {form.config.authType === 'basic' && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Username"
                  value={form.config.username}
                  onChange={(e) => updateForm('config.username', e.target.value)}
                  disabled={loading}
                  placeholder="Username per Basic Auth"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Password"
                  value={form.config.password}
                  onChange={(e) => updateForm('config.password', e.target.value)}
                  disabled={loading}
                  type="password"
                  placeholder="Password per Basic Auth"
                />
              </Grid>
            </>
          )}

          {/* Codici */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Codice NSIS"
              value={form.config.nsisCode}
              onChange={(e) => updateForm('config.nsisCode', e.target.value)}
              disabled={loading}
              placeholder="202011400"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Authority ID"
              value={form.config.authorityId}
              onChange={(e) => updateForm('config.authorityId', e.target.value)}
              disabled={loading}
              placeholder="STRPRIV45"
            />
          </Grid>

          {!profile.isCustom && (
            <Grid item xs={12}>
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Nota:</strong> Per i profili predefiniti, le modifiche alle credenziali 
                  richiedono il riavvio del backend per essere effettive. Le modifiche agli URL 
                  sono immediate.
                </Typography>
              </Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Annulla
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={loading}
        >
          {loading ? (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={16} />
              Aggiornamento...
            </Box>
          ) : (
            'Salva Modifiche'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProfileEditDialog;